import { dependabilityV2Colors } from '@/styles/colors';
import { ringGradient, scoreColor } from './tierColors';
import { clampScore, displayScore } from './score';

interface Props {
  score: number;
  tier: string;
  size?: number;
}

const ScoreRing = ({ score, tier, size = 100 }: Props) => {
  // Rendered Figma export: 100-box with outer radius 50 and inner 40.6.
  const strokeWidth = size * 0.094;
  const radius = (size - strokeWidth) / 2;
  const circumference = 2 * Math.PI * radius;
  const clamped = clampScore(score);
  const center = size / 2;
  const shownScore = displayScore(score);
  const gradient = ringGradient(tier);
  const gradientId = `dep-ring-gradient-${tier}`;

  return (
    <svg
      width={size}
      height={size}
      role="img"
      aria-label={`Dependability score ${shownScore} out of 100`}
    >
      {gradient && (
        <defs>
          {/* Export coords are page-space (6.5,35.5)→(60,5); the arc circle is
              rotated -90°, so they're counter-rotated into its local space. */}
          <linearGradient
            id={gradientId}
            gradientUnits="userSpaceOnUse"
            x1={size * 0.645}
            y1={size * 0.065}
            x2={size * 0.95}
            y2={size * 0.6}
          >
            <stop stopColor={gradient[0]} />
            <stop offset="1" stopColor={gradient[1]} />
          </linearGradient>
        </defs>
      )}
      <circle
        cx={center}
        cy={center}
        r={radius}
        fill="none"
        stroke={dependabilityV2Colors.depMeterTrack}
        strokeWidth={strokeWidth}
      />
      <circle
        data-testid="score-ring-progress"
        cx={center}
        cy={center}
        r={radius}
        fill="none"
        stroke={gradient ? `url(#${gradientId})` : scoreColor(tier)}
        strokeWidth={strokeWidth}
        strokeLinecap="round"
        strokeDasharray={circumference}
        strokeDashoffset={circumference * (1 - clamped / 100)}
        transform={`rotate(-90 ${center} ${center})`}
      />
      <text
        x={center}
        y={center}
        textAnchor="middle"
        dominantBaseline="central"
        fontSize={size * 0.267}
        fontWeight={600}
        fill={scoreColor(tier)}
      >
        {shownScore}
      </text>
    </svg>
  );
};

export default ScoreRing;
